import React, { useEffect, useState } from 'react';
import { InputProp } from '../../utils/tsConfig';

interface DeclarationProp extends InputProp {
  declarationText?: string;
  declarationLink?: string;
}

const Declare: React.FC<DeclarationProp> = (props) => {
  const {
    keyId,
    setState,
    state,
    isArray,
    width,
    indexVal,
    arrayName,
    className,
    returnKey,
    fieldName,
    onChange,
    required = false,
    disabled = false,
    declarationText = '',
    declarationLink = '',
    ...otherProps
  } = props;

  const [linkVisited, setLinkVisited] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (!state || typeof state !== 'object') return;

    const val = isArray && arrayName && indexVal !== undefined && returnKey
      ? state?.[arrayName]?.[indexVal]?.[returnKey]
      : returnKey
      ? state?.[returnKey]
      : false;

    setAgreed(!!val);
  }, [state]);

  const updateState = (value: boolean) => {
    if (isArray && arrayName && indexVal !== undefined && returnKey && setState) {
      const updated = { ...state };
      if (!updated[arrayName]) updated[arrayName] = [];
      while (updated[arrayName].length <= indexVal) updated[arrayName].push({});
      updated[arrayName][indexVal][returnKey] = value;
      setState(updated);
    } else {
      onChange?.({ value: String(value) });
      if (returnKey && setState) setState({ ...state, [returnKey]: value });
    }
  };

  const handleLinkVisit = () => {
    setLinkVisited(true);
    if (declarationLink) {
      window.open(declarationLink, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setAgreed(isChecked);
    updateState(isChecked);
  };

  const isButtonEnabled = linkVisited && agreed;

  return (
    <div className="max-w-2xl mx-auto p-8 bg-white rounded-2xl border border-gray-100 shadow-lg">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {fieldName?.replace(/\*$/, '') || 'Declaration'}
        </h2>
        {fieldName?.endsWith('*') && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
            Required
          </span>
        )}
      </div>

      {/* Content Section */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <p className="text-gray-700 leading-relaxed text-center">
          {declarationText || 'Please read and accept the declaration before proceeding.'}
        </p>
      </div>

      {/* Link Section */}
      {declarationLink && (
        <div className="mb-6">
          {!linkVisited ? (
            <button
              type="button"
              onClick={handleLinkVisit}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl text-blue-700 font-medium transition-all duration-200 hover:shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View Full Declaration Document
            </button>
          ) : (
            <div className="flex items-center justify-center gap-3 py-3 px-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-green-700 font-medium">Declaration document reviewed</span>
            </div>
          )}
        </div>
      )}

      {/* Agreement Section */}
      <div className="mb-8">
        <label className="flex items-start gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-300 transition-colors cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              checked={agreed}
              onChange={handleCheck}
              disabled={!linkVisited || disabled}
              required={required}
              className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {agreed && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex-1">
            <span className="text-gray-800 font-medium group-hover:text-blue-700 transition-colors">
              I acknowledge and agree to the declaration above
            </span>
            <p className="text-sm text-gray-500 mt-1">
              By checking this box, you confirm your understanding and acceptance
            </p>
          </div>
        </label>
      </div>

      {/* Action Button */}
      <button
        type="button"
        disabled={!isButtonEnabled}
        className={`w-full py-4 px-6 rounded-xl text-lg font-semibold transition-all duration-300 transform ${
          isButtonEnabled
            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-lg hover:scale-[1.02] focus:ring-4 focus:ring-blue-200'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isButtonEnabled ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Declaration Complete - Proceed
          </span>
        ) : (
          'Complete Declaration to Continue'
        )}
      </button>

      {/* Footer Note */}
      <p className="text-xs text-gray-500 text-center mt-4">
        This declaration is required to proceed. All information is handled securely.
      </p>
    </div>
  );
};

export default Declare;
